import {
    Box,
    Button,
    Flex,
    Heading,
    Icon,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Text,
    VStack,
    Spinner,
    Alert,
} from "@chakra-ui/react";
import { FiUploadCloud } from "react-icons/fi";
import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import axiosInstance from "services/axiosInstance";
import { ENDPOINTS } from "api/endpoints";
import { useAppSelector } from "hooks/store";
import { selectUserData } from "store/auth/authSlice";
import { AxiosError } from "axios";

interface IUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function UploadModal({ isOpen, onClose }: IUploadModalProps) {
    
    const authData = useAppSelector(selectUserData);
    
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Dropzone callback
    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            const selectedFile = acceptedFiles[0];
            if (!selectedFile.name.endsWith(".csv")) {
                toast.error("Invalid file type. Please upload a CSV file.");
                return;
            }

            setFile(selectedFile);
            toast.success("File ready for upload");
        },
        [toast],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: false,
        accept: { "text/csv": [".csv"] },
    });

    // Upload handler
    const handleUpload = async () => {
        if (!file) return;

        setLoading(true);
        const formData = new FormData();

        formData.append("file", file);
        formData.append("dealership_uid", authData.dealerV2.uid);

        try {
            const response = await axiosInstance.post(ENDPOINTS.UPLOAD_SOLD_FILE, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            toast.success("File uploaded successfully");
            

            handleClose(); // clear file on success too
        } catch (error: any) {
            if (error instanceof AxiosError) {
                toast.error(
                    error.response?.data?.error || error.message || "An unexpected error occurred. Please try again.",
                );
            }
        } finally {
            setLoading(false);
        }
    };

    // Reset file and close modal
    const handleClose = () => {
        setFile(null); // clear selected file
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent borderRadius="2xl" p={4}>
                <ModalHeader>
                    <Heading size="md">Upload Sales CSV</Heading>
                    <Text fontSize="sm" color="gray.500" mt={1}>
                        Import your dealership sales data quickly and securely.
                    </Text>
                </ModalHeader>

                <ModalCloseButton onClick={handleClose} />

                <ModalBody>
                    <VStack spacing={6}>
                        {/* Drag & Drop Area */}
                        <Box
                            {...getRootProps()}
                            w="100%"
                            border="2px dashed"
                            borderColor={isDragActive ? "blue.400" : "blue.300"}
                            borderRadius="xl"
                            p={10}
                            textAlign="center"
                            cursor="pointer"
                            _hover={{ bg: "blue.50" }}
                            transition="0.2s"
                        >
                            <input {...getInputProps()} />
                            <VStack spacing={4}>
                                <Icon as={FiUploadCloud} boxSize={10} color="blue.400" />
                                <Text fontWeight="medium">
                                    {file ? file.name : "Click to upload or drag & drop"}
                                </Text>
                                <Text fontSize="sm" color="gray.500">
                                    Only CSV files are supported
                                </Text>
                            </VStack>
                        </Box>
                    </VStack>
                </ModalBody>

                <ModalFooter>
                    <Flex w="100%" justify="space-between">
                        <Button variant="ghost" onClick={handleClose}>
                            Cancel
                        </Button>

                        <Button
                            colorScheme="blue"
                            borderRadius="xl"
                            px={8}
                            onClick={handleUpload}
                            disabled={!file || loading}
                        >
                            {loading && <Spinner size="sm" mr={2} />}
                            Upload File
                        </Button>
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
